/** Spec §14.2 page 2: searchable inventory with reversible allow/deny/quarantine + results. */
import { useState } from "react";

import { useAppRule, useApps } from "../api/hooks.js";
import { StateBadge } from "../components/StateBadge.js";
import { Empty, PageState } from "../components/PageState.js";

const STATES = ["allowed", "denied", "quarantined", "unrated"] as const;

export function GameLibraryPage({ deviceId }: { deviceId: string }) {
  const apps = useApps(deviceId);
  const rule = useAppRule(deviceId);
  const [filter, setFilter] = useState("");

  if (!deviceId) return <Empty label="No device selected." />;
  const rows = (apps.data ?? []).filter(
    (a) => !filter || a.package_id.includes(filter) || (a.display_name ?? "").toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <PageState query={{ isPending: apps.isPending, error: apps.error }}>
      <input
        aria-label="Search games"
        placeholder="Search installed titles"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />
      {rows.length === 0 ? (
        <p className="notice">No matching third-party apps in inventory.</p>
      ) : (
        <table>
          <thead>
            <tr><th>Title</th><th>Package</th><th>Version</th><th>Observed</th><th>State</th><th>Action</th></tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.package_id}>
                <td>{a.display_name ?? "—"}</td>
                <td className="mono">{a.package_id}</td>
                <td>{a.version ?? "—"}</td>
                <td>{new Date(a.observed_at).toLocaleDateString()}</td>
                <td><StateBadge state={a.allow_state} /></td>
                <td>
                  <select
                    aria-label={`Set state for ${a.package_id}`}
                    value={a.allow_state}
                    disabled={rule.isPending}
                    onChange={(e) => rule.mutate({ packageId: a.package_id, allowState: e.target.value })}
                  >
                    {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="muted">
        Changes are stored as policy and applied by the headset agent on next contact — an app force-stop is
        not a persistent block, so the state column reflects the rule, not the running process.
      </p>
    </PageState>
  );
}
